import { useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { ChartBox, chartDataset } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { CrDailyRow, CrFlag, CrRateKey, CrResponse, CrSourceKey, CrStaffRateKey, CrStoreRow, Tone } from '../types';
import {
  CR_MIN_VPAGE_MATCH, CR_THRESHOLDS, RATE_BY_KEY, RATES, STAGE_BY_CODE, STAGE_TONE, VPAGE_GAP_NOTE, deltaTone, formatDelta,
  isVpageGap, rag, sourceBadge, tierLabel, tierTone, type RagTone, type StageCode, type Threshold,
} from '../lib/identity';

interface CrScreenProps {
  data: CrResponse | null;
  loaded: boolean;
  busy: boolean;
  range: string;
  onRange: (range: string) => void;
}

const PRESETS = [
  { v: '7d', l: '7 ngày' },
  { v: '28d', l: '28 ngày' },
  { v: '90d', l: '90 ngày' },
];
const KPI_KEYS: CrRateKey[] = ['cr_store', 'cr_instore', 'cr_care', 'coverage', 'contact', 'interaction'];
const FUNNEL: StageCode[] = ['V', 'R', 'P', 'T', 'C'];
const STAFF_RATES: CrStaffRateKey[] = ['contact', 'interaction', 'close_tag', 'cr_care'];
const SOURCES: Array<{ key: CrSourceKey; label: string }> = [
  { key: 'palexy', label: 'Palexy' },
  { key: 'fsales', label: 'f_sales' },
  { key: 'vpage', label: 'Vpage' },
  { key: 'captures', label: 'Ghi nhận' },
];
// Chuỗi xu hướng: O tách thành đơn ≥ D+1 (kết quả chăm sóc) và đơn cùng ngày (mua tại quầy chưa gắn cờ).
type SeriesKey = 'V' | 'R' | 'P' | 'T' | 'C' | 'O' | 'SD';
const SERIES: SeriesKey[] = ['V', 'R', 'P', 'T', 'C', 'O', 'SD'];
const LINE_COLORS: Record<SeriesKey, string> = { R: '#2563eb', P: '#0d9488', T: '#7c3aed', C: '#64748b', O: '#16a34a', SD: '#d97706', V: '#0891b2' };
const SERIES_LABEL: Record<SeriesKey, string> = {
  V: `V · ${STAGE_BY_CODE.V.label}`, R: `R · ${STAGE_BY_CODE.R.label}`, P: `P · ${STAGE_BY_CODE.P.label}`,
  T: `T · ${STAGE_BY_CODE.T.label}`, C: `C · ${STAGE_BY_CODE.C.label}`, O: 'O · Đơn ≥ D+1', SD: 'Đơn cùng ngày',
};
const RAG_TEXT: Record<RagTone, string> = { green: 'Đạt', amber: 'Theo dõi', red: 'Dưới ngưỡng', gray: 'Chưa xếp' };
const COVERAGE_NOTE = '>100% — lượt khách Palexy thấp hơn số ghi nhận';
const GAP_NOTE = 'đơn xác minh vượt tag — tag chưa cập nhật';
const VPAGE_LOW_NOTE = `SĐT khớp Vpage < ${CR_MIN_VPAGE_MATCH}`;
const SAME_DAY_LABEL = 'Đơn cùng ngày (chưa gắn bán tại quầy)';

const num = (v: number | null | undefined) => (v == null ? '–' : Number(v).toLocaleString('vi-VN'));
const pct = (v: number | null | undefined) => (v == null || !Number.isFinite(Number(v)) ? '–' : `${Number(v).toFixed(1).replace('.', ',')}%`);

interface RateContext {
  flags?: CrFlag[];
  with_phone?: number;
  phone_matched_vpage?: number;
}
interface RateView { text: string; tone: Tone; label: string; note: string }

// Bất thường số liệu thật: hiện trung thực + ghi chú, không tô màu xếp hạng.
function rateCell(key: string, value: number | null | undefined, thresholds: Threshold[], ctx?: RateContext): RateView {
  if (key === 'interaction' && value == null && ctx && (ctx.with_phone ?? 0) > 0 && (ctx.phone_matched_vpage ?? 0) < CR_MIN_VPAGE_MATCH) {
    const b = sourceBadge('no_data');
    return { text: b.label, tone: b.tone, label: b.label, note: VPAGE_LOW_NOTE };
  }
  if (key === 'verify_gap' && value == null && ctx?.flags?.includes('verify_gap_over_tag')) {
    return { text: '–', tone: 'gray', label: RAG_TEXT.gray, note: GAP_NOTE };
  }
  if (key === 'coverage' && value != null && value > 100) return { text: pct(value), tone: 'gray', label: RAG_TEXT.gray, note: COVERAGE_NOTE };
  const tone = rag(key, value, thresholds);
  return { text: pct(value), tone, label: RAG_TEXT[tone], note: '' };
}
// verify_gap: giảm là tốt → đảo màu delta.
const deltaToneFor = (key: string, d: number | null | undefined) => deltaTone(key === 'verify_gap' && d != null ? -d : d);

function Sparkline({ values }: { values: number[] }) {
  const w = 120;
  const h = 28;
  if (!values.length) return <span className="muted">–</span>;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const pts = values.map((v, i) => `${(i * step).toFixed(1)},${(h - 2 - (v / max) * (h - 4)).toFixed(1)}`).join(' ');
  return (
    <svg className="cr-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-label="Ghi nhận 28 ngày">
      <polyline points={pts} fill="none" stroke="#2563eb" strokeWidth="1.5" />
    </svg>
  );
}

type FunnelCounts = Pick<CrDailyRow, 'visits' | 'captures' | 'with_phone' | 'interacted' | 'closed_tag' | 'orders_followup' | 'orders_same_day'>;

function seriesValue(row: FunnelCounts, key: SeriesKey): number {
  if (key === 'O') return Number(row.orders_followup || 0);
  if (key === 'SD') return Number(row.orders_same_day || 0);
  const metric = STAGE_BY_CODE[key].metric as keyof FunnelCounts;
  return Number(row[metric] || 0);
}

function storeNotes(s: CrStoreRow): string[] {
  const notes: string[] = [];
  if ((s.rates.coverage ?? 0) > 100) notes.push(`Độ phủ ${COVERAGE_NOTE}`);
  if (s.flags?.includes('verify_gap_over_tag')) notes.push(`Lệch tự báo: ${s.verify_gap_note || GAP_NOTE}`);
  if (s.visits_missing_days) notes.push(`Thiếu Palexy ${s.visits_missing_days} ngày — đã loại khỏi mẫu V`);
  return notes;
}

export function CrScreen({ data, loaded, busy, range, onRange }: CrScreenProps) {
  const [selected, setSelected] = useState('');
  const [from, setFrom] = useState(data?.meta.period?.start || '');
  const [to, setTo] = useState(data?.meta.period?.end || '');

  const thresholds: Threshold[] = data?.thresholds?.length ? data.thresholds : CR_THRESHOLDS;
  const stores = data?.stores || [];
  const daily = data?.daily || [];
  const dates = useMemo(() => [...new Set(daily.map(d => d.ngay))].sort(), [daily]);
  const sparkDates = dates.slice(-28);
  const byStoreDay = useMemo(() => {
    const m = new Map<string, CrDailyRow>();
    daily.forEach(d => m.set(`${d.store_code}|${d.ngay}`, d));
    return m;
  }, [daily]);

  const trend = useMemo(() => {
    const series = Object.fromEntries(SERIES.map(k => [k, dates.map(() => 0)])) as Record<SeriesKey, number[]>;
    daily.forEach(d => {
      if (selected && d.store_code !== selected) return;
      const i = dates.indexOf(d.ngay);
      SERIES.forEach(k => { series[k][i] += seriesValue(d, k); });
    });
    return series;
  }, [daily, dates, selected]);

  if (busy && !loaded) return <div className="card empty">Đang tải CR &amp; hiệu quả chăm sóc...</div>;
  if (!loaded || !data) return <div className="card empty">Mở màn CR để tải dữ liệu.</div>;

  const sys = data.system || { rates: {}, delta: {} };
  const selectedName = stores.find(s => s.store_code === selected)?.store_name || '';
  const staff = data.staff
    .filter(s => !selected || s.store_code === selected)
    .sort((a, b) => b.captures - a.captures);
  const hasGap = dates.some(isVpageGap);
  const gapMax = Math.max(1, ...trend.R, ...trend.P, ...trend.T, ...trend.C, ...trend.O, ...trend.SD);
  const datasets: ChartDataset[] = [
    ...(hasGap ? [{
      label: 'Vpage trắng', type: 'bar', yAxisID: 'y', data: dates.map(d => (isVpageGap(d) ? gapMax : 0)),
      backgroundColor: 'rgba(148,163,184,0.18)', borderWidth: 0, barPercentage: 1, categoryPercentage: 1, order: 10,
      datalabels: { display: false },
    } as ChartDataset] : []),
    ...(['R', 'P', 'T', 'C', 'O', 'SD'] as SeriesKey[]).map(k => chartDataset(SERIES_LABEL[k], trend[k], LINE_COLORS[k], 'line')),
    chartDataset(SERIES_LABEL.V, trend.V, LINE_COLORS.V, 'line', 'y2'),
  ];

  return (
    <>
      <section className="filters cr-filters">
        <div className="actions start">
          <span className="kpi-label">Kỳ</span>
          {PRESETS.map(p => <button key={p.v} className={`tab ${range === p.v ? 'active' : ''}`} onClick={() => onRange(p.v)}>{p.l}</button>)}
        </div>
        <div className="actions start cr-dates">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} aria-label="Từ ngày" />
          <span className="muted">→</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} aria-label="Đến ngày" />
          <button className={`tab ${range.includes('..') ? 'active' : ''}`} onClick={() => onRange(`${from}..${to}`)} disabled={!from || !to || from > to}>Áp dụng</button>
          {busy && <span className="muted">Đang tải…</span>}
        </div>
        <div className="muted cr-period">
          {data.meta.period ? `${data.meta.period.start} → ${data.meta.period.end}` : data.meta.range} · so với {data.meta.prev_range}
        </div>
      </section>

      {data.meta.live === false && (
        <section className="card cr-offline">
          <strong>Mart CR chưa có dữ liệu — đợi ETL</strong>
          {data.meta.error && <div className="muted">{data.meta.error}</div>}
        </section>
      )}

      <section className="grid kpis cr-kpis">
        {KPI_KEYS.map(key => {
          const value = sys.rates[key];
          const d = sys.delta[key];
          const c = rateCell(key, value, thresholds, sys);
          const sub = key === 'cr_care'
            ? `${num(sys.orders_followup)} đơn ≥ D+1 · ${num(sys.orders_same_day)} đơn cùng ngày · median store ${pct(data.benchmark?.[key])}`
            : `median store: ${pct(data.benchmark?.[key])} · ${RATE_BY_KEY[key].formula}`;
          return (
            <div key={key} className="cr-kpi">
              <KpiCard label={RATE_BY_KEY[key].label} value={c.text} sub={sub} />
              <div className="cr-kpi-foot">
                <Tag tone={c.tone}>{c.label}</Tag>
                <Tag tone={deltaToneFor(key, d)}>{formatDelta(d)}</Tag>
                {c.note && <span className="cr-note">{c.note}</span>}
              </div>
            </div>
          );
        })}
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Xếp hạng store</h2>
          <span className="muted">Bấm một hàng để lọc xu hướng và nhân viên{selected ? ` · đang chọn ${selectedName}` : ''}</span>
          {selected && <button className="tab" onClick={() => setSelected('')}>Bỏ chọn</button>}
        </div>
        <div className="scroll cr-table">
          <table>
            <thead>
              <tr>
                <th>#</th><th>Store</th><th>Hạng</th>
                {RATES.map(r => <th key={r.key} title={`${r.label} (${r.formula})`}>{r.short}</th>)}
                <th title={SAME_DAY_LABEL}>Đơn cùng ngày<span className="cr-th-sub">chưa gắn bán tại quầy</span></th>
                <th>Ghi nhận 28 ngày</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((s: CrStoreRow) => (
                <tr key={s.store_code} className={`cr-row ${selected === s.store_code ? 'active' : ''}`} onClick={() => setSelected(selected === s.store_code ? '' : s.store_code)}>
                  <td className="tnum">{s.rank}</td>
                  <td><strong>{s.store_name}</strong></td>
                  <td><Tag tone={tierTone(s.tier)}>{s.tier} · {tierLabel(s.tier)}</Tag></td>
                  {RATES.map(r => {
                    const c = rateCell(r.key, s.rates[r.key], thresholds, s);
                    const d = s.delta?.[r.key];
                    return (
                      <td key={r.key} className="cr-rate" title={c.note || undefined}>
                        <Tag tone={c.tone}>{c.text}</Tag>
                        <span className={`cr-delta ${deltaToneFor(r.key, d)}`}>{formatDelta(d)}</span>
                        {c.note && <span className="cr-note">{c.note}</span>}
                      </td>
                    );
                  })}
                  <td className="tnum" title={`${num(s.orders_same_day)} / ${num(s.orders_verified)} đơn xác minh ≤30 ngày là đơn cùng ngày ghi nhận`}>
                    <strong>{num(s.orders_same_day)}</strong>
                    <span className="cr-note">/ {num(s.orders_verified)} đơn xác minh</span>
                  </td>
                  <td><Sparkline values={sparkDates.map(day => byStoreDay.get(`${s.store_code}|${day}`)?.captures || 0)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="cr-multiples">
        {stores.map(s => {
          const v = s.visits || 0;
          const share = (val: number) => (v ? (val / v) * 100 : null);
          const qShare = share(s.instore_sales) ?? 0;
          const sdShare = share(s.orders_same_day) ?? 0;
          return (
            <div key={s.store_code} className={`card cr-funnel ${selected === s.store_code ? 'active' : ''}`} onClick={() => setSelected(s.store_code)}>
              <div className="section-title"><h2>{s.store_name}</h2><Tag tone={tierTone(s.tier)}>{s.tier}</Tag></div>
              {FUNNEL.map(code => {
                const val = seriesValue(s, code as SeriesKey);
                return (
                  <div key={code} className="cr-bar-row">
                    <Tag tone={STAGE_TONE[code]}>{code}</Tag>
                    <div className="cr-bar"><div className={`cr-bar-fill ${STAGE_TONE[code]}`} style={{ width: `${Math.min(100, share(val) ?? 0)}%` }} /></div>
                    <span className="tnum cr-bar-val">{num(val)} · {code === 'V' ? '100%' : pct(share(val))}</span>
                  </div>
                );
              })}
              <div className="cr-bar-row" title="Đơn xác minh ≥ D+1 sau ngày ghi nhận — kết quả chăm sóc">
                <Tag tone={STAGE_TONE.O}>O</Tag>
                <div className="cr-bar"><div className="cr-bar-fill green" style={{ width: `${Math.min(100, share(s.orders_followup) ?? 0)}%` }} /></div>
                <span className="tnum cr-bar-val">{num(s.orders_followup)} ≥ D+1 · {pct(share(s.orders_followup))}</span>
              </div>
              <div className="cr-bar-row" title={`Q bán tại quầy ${num(s.instore_sales)} + ${SAME_DAY_LABEL.toLowerCase()} ${num(s.orders_same_day)}`}>
                <Tag tone={STAGE_TONE.Q}>Q</Tag>
                <div className="cr-bar">
                  <div className="cr-bar-fill indigo" style={{ width: `${Math.min(100, qShare)}%` }} />
                  <div className="cr-bar-fill same-day" style={{ width: `${Math.max(0, Math.min(100 - qShare, sdShare))}%` }} />
                </div>
                <span className="tnum cr-bar-val">{num(s.instore_sales)} + {num(s.orders_same_day)} · {pct(qShare + sdShare)}</span>
              </div>
              <div className="cr-legend">
                <span><i className="cr-bar-fill indigo" />Bán tại quầy</span>
                <span><i className="cr-bar-fill same-day" />Đơn cùng ngày (chưa gắn)</span>
              </div>
              {storeNotes(s).map(note => <div key={note} className="cr-note">{note}</div>)}
            </div>
          );
        })}
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Xu hướng theo ngày · {selectedName || 'toàn hệ thống'}</h2>
          {hasGap && <Tag tone="gray">{VPAGE_GAP_NOTE}</Tag>}
        </div>
        <div className="chart tall">
          <ChartBox
            labels={dates.map(d => d.slice(5))}
            datasets={datasets}
            options={{
              plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, filter: item => item.text !== 'Vpage trắng' } } },
              scales: {
                x: { stacked: true, grid: { display: false } },
                y: { beginAtZero: true, title: { display: true, text: 'R/P/T/C/O/cùng ngày' } },
                y2: { beginAtZero: true, position: 'right', grid: { display: false }, title: { display: true, text: 'V' } },
              },
            }}
          />
        </div>
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Nhân viên{selectedName ? ` · ${selectedName}` : ''}</h2>
          <span className="muted">{staff.length} người · sắp theo ghi nhận</span>
        </div>
        <div className="scroll cr-table">
          <table>
            <thead>
              <tr>
                <th>Nhân viên</th><th>Store</th><th>R</th><th>P</th><th>T</th><th>C</th><th title="Đơn xác minh ≥ D+1">O ≥ D+1</th><th title={SAME_DAY_LABEL}>Cùng ngày</th><th>Q</th>
                {STAFF_RATES.map(k => <th key={k}>{RATE_BY_KEY[k].short}</th>)}
              </tr>
            </thead>
            <tbody>
              {staff.map(s => (
                <tr key={`${s.store_code}-${s.staff_id}`}>
                  <td>{s.staff_name}</td>
                  <td className="muted">{s.store_code}</td>
                  <td className="tnum">{num(s.captures)}</td>
                  <td className="tnum">{num(s.with_phone)}</td>
                  <td className="tnum">{num(s.interacted)}</td>
                  <td className="tnum">{num(s.closed_tag)}</td>
                  <td className="tnum">{num(s.orders_followup)}</td>
                  <td className="tnum">{num(s.orders_same_day)}</td>
                  <td className="tnum">{num(s.instore_sales)}</td>
                  {STAFF_RATES.map(k => {
                    const c = rateCell(k, s.rates[k], thresholds, s);
                    return <td key={k} title={c.note || undefined}><Tag tone={c.tone}>{c.text}</Tag></td>;
                  })}
                </tr>
              ))}
              {!staff.length && <tr><td colSpan={13} className="muted">Không có nhân viên trong kỳ.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card cr-sources">
        <span className="kpi-label">Nguồn dữ liệu</span>
        {SOURCES.map(src => {
          const b = sourceBadge(data.meta.sources?.[src.key]);
          return <span key={src.key} title={b.note}><Tag tone={b.tone}>{src.label}: {b.label}</Tag></span>;
        })}
        <span className="muted">Cập nhật {data.meta.refreshed_at ? new Date(data.meta.refreshed_at).toLocaleString('vi-VN') : '–'}{data.meta.fixture ? ' · dữ liệu mẫu' : ''}</span>
      </section>
    </>
  );
}
